<template>
    <vx-expandable-card>
        <template v-slot:content>
            <div class="flex flex-col gap-2 mb-4 sm:flex-row sm:items-center sm:justify-between">
                <div class="flex items-center gap-2">
                    <span class="text-xl font-extrabold">Numbers</span>
                    <vx-badge v-if="numbers" color="slate" size="xs" data-test="numbers-count">
                        {{ numbers.data.length }} / {{ numbers.limit }}
                    </vx-badge>
                    <vx-badge v-if="failedCount" color="danger" size="xs" data-test="failed-count">
                        {{ failedCount }} failed
                    </vx-badge>
                </div>
                <vx-popover hover placement="top" arrow :disabled="null === attachDisabledReason">
                    <vx-button
                        @click="attaching = true"
                        size="sm"
                        color="primary"
                        data-test="attach-numbers"
                        :disabled="null === numbers || null !== attachDisabledReason"
                    >
                        Attach numbers <font-awesome-icon widthAuto :icon="faPlus" />
                    </vx-button>
                    <template v-slot:content>
                        <span>{{ attachDisabledReason }}</span>
                    </template>
                </vx-popover>
            </div>

            <vx-alert
                v-if="numbers?.bandwidth.error"
                color="danger"
                :closeable="false"
                class="mb-4"
                data-test="bandwidth-error"
            >
                {{ numbers.bandwidth.error }}
            </vx-alert>

            <page-loading v-if="loading && null === numbers"></page-loading>

            <vx-table v-else-if="numbers?.data.length" class="xl:grid-cols-6-auto" rowBreakpoint="xl">
                <vx-table-row isHeader>
                    <div>Phone Number</div>
                    <div>Status</div>
                    <div>NNID</div>
                    <div>Team</div>
                    <div>Last order</div>
                </vx-table-row>
                <vx-table-row
                    v-for="row in numbers.data"
                    :key="row.phone_number"
                    data-test="number-row"
                    :data-phone="row.phone_number"
                >
                    <vx-table-row-title>{{ formatPhoneNumber(row.phone_number) }}</vx-table-row-title>
                    <vx-table-row-item>
                        <template v-slot:label> Status: </template>
                        <vx-badge
                            v-if="null === row.status"
                            color="slate"
                            size="xs"
                            data-test="registration-status"
                            title="The last sync put this number on the campaign; Bandwidth has not confirmed it."
                        >
                            Unconfirmed
                        </vx-badge>
                        <vx-badge
                            v-else
                            :color="registrationStatusColors[row.status] ?? 'slate'"
                            size="xs"
                            data-test="registration-status"
                        >
                            {{ registrationStatusLabels[row.status] ?? row.status }}
                        </vx-badge>
                    </vx-table-row-item>
                    <vx-table-row-item>
                        <template v-slot:label> NNID: </template>
                        <span v-if="row.nnid" class="font-mono">{{ row.nnid }}</span>
                        <span v-else>—</span>
                    </vx-table-row-item>
                    <vx-table-row-item data-test="team">
                        <template v-slot:label> Team: </template>
                        <kiosk-team-cell
                            v-if="null !== row.team_id"
                            :teamId="row.team_id"
                            :teamName="row.team_name ?? ''"
                            :teamStatus="row.team_status ?? ''"
                            :teamStatusReason="row.team_status_reason"
                        />
                        <span v-else>—</span>
                    </vx-table-row-item>
                    <vx-table-row-item>
                        <template v-slot:label> Last order: </template>
                        <div v-if="row.last_order" class="flex flex-col gap-1" data-test="last-order">
                            <div class="flex items-center gap-2">
                                <vx-badge
                                    :color="phoneNumberOrderStatusColors[row.last_order.status]"
                                    size="xs"
                                    data-test="last-order-status"
                                >
                                    {{ phoneNumberOrderStatusLabels[row.last_order.status] }}
                                </vx-badge>
                                <span
                                    v-if="orderElsewhere(row.last_order)"
                                    class="text-slate-500"
                                    data-test="last-order-campaign"
                                >
                                    to {{ row.last_order.campaign_id }}
                                </span>
                                <span :title="shortDateTime(orderTime(row.last_order))">
                                    {{ fromNow(orderTime(row.last_order)) }}
                                </span>
                            </div>
                            <div v-if="orderFailed(row.last_order)" class="text-rose-600" data-test="last-order-error">
                                {{ row.last_order.error_description }}
                                <span
                                    v-if="null !== row.last_order.error_code"
                                    class="font-mono"
                                    data-test="last-order-code"
                                >
                                    {{ row.last_order.error_code }}
                                </span>
                            </div>
                            <div v-else-if="orderStale(row.last_order)" class="text-amber-600" data-test="stale-order">
                                Still processing at Bandwidth
                            </div>
                        </div>
                        <span v-else>—</span>
                    </vx-table-row-item>
                    <vx-table-row-buttons>
                        <vx-button @click="detaching = row" size="sm" data-test="detach" outline color="danger">
                            Detach <font-awesome-icon widthAuto :icon="faLinkSlash" />
                        </vx-button>
                    </vx-table-row-buttons>
                </vx-table-row>
            </vx-table>

            <div v-else-if="numbers" class="text-sm text-slate-500" data-test="numbers-empty">
                No numbers on this campaign yet.
            </div>

            <div v-if="numbers?.orders.length" class="mt-4 flex flex-col gap-2" data-test="unattached-orders">
                <div class="font-bold">Pending and failed attachments</div>
                <div
                    v-for="row in numbers.orders"
                    :key="`${row.order_id}-${row.phone_number}`"
                    class="flex flex-wrap items-center gap-2"
                    data-test="unattached-order-row"
                    :data-phone="row.phone_number"
                >
                    <span>{{ formatPhoneNumber(row.phone_number) }}</span>
                    <span class="font-mono">{{ row.order_id }}</span>
                    <vx-badge
                        :color="phoneNumberOrderStatusColors[row.status]"
                        size="xs"
                        data-test="unattached-order-status"
                    >
                        {{ phoneNumberOrderStatusLabels[row.status] }}
                    </vx-badge>
                    <span v-if="orderFailed(row)" class="text-rose-600" data-test="unattached-order-error">
                        {{ row.error_description }}
                        <span v-if="null !== row.error_code" class="font-mono">{{ row.error_code }}</span>
                    </span>
                    <span v-else-if="orderStale(row)" class="text-amber-600" data-test="unattached-order-stale">
                        Still processing at Bandwidth
                    </span>
                    <span class="text-slate-500" :title="shortDateTime(orderTime(row))">
                        {{ fromNow(orderTime(row)) }}
                    </span>
                </div>
            </div>

            <vx-alert
                v-if="order && !orderSettled"
                color="info"
                :closeable="false"
                class="mt-4"
                data-test="order-status"
            >
                <div class="flex flex-col gap-2">
                    <div class="font-bold">
                        Order {{ order.order_id }}: {{ tnOptionOrderStatusLabels[order.status] ?? order.status }}
                    </div>
                    <div data-test="order-numbers">{{ order.phone_numbers.map(formatPhoneNumber).join(', ') }}</div>
                    <ul v-if="order.errors.length" class="list-disc pl-5" data-test="order-errors">
                        <li v-for="(error, index) in order.errors" :key="index">{{ orderErrorLine(error) }}</li>
                    </ul>
                    <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <span>
                            Bandwidth applies the order in the background; reload in a moment to see the result.
                        </span>
                        <vx-button @click="load()" outline size="sm" data-test="reload-numbers" :loading="loading">
                            Reload <font-awesome-icon widthAuto :icon="faArrowsRotate" />
                        </vx-button>
                    </div>
                </div>
            </vx-alert>

            <kiosk-tcr-campaign-attach-modal
                v-if="numbers"
                v-model:visible="attaching"
                :campaignId="props.campaignId"
                :brandTeamIds="numbers.brand_team_ids"
                :attachedNumbers="numbers.data.map((row) => row.phone_number)"
                :limit="numbers.limit"
                @placed="onPlaced"
            />

            <kiosk-tcr-number-detach-modal
                v-if="detaching"
                v-model:visible="detachModal"
                :campaignId="props.campaignId"
                :phoneNumber="detaching.phone_number"
                @placed="onPlaced"
                @stale="onStale"
            />
        </template>
    </vx-expandable-card>
</template>

<script lang="ts" setup>
import { faArrowsRotate, faLinkSlash, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxAlert,
    VxBadge,
    VxButton,
    VxExpandableCard,
    VxPopover,
    VxTable,
    VxTableRow,
    VxTableRowButtons,
    VxTableRowItem,
    VxTableRowTitle,
} from '@voxie/frontend-components';
import { isCancel } from 'axios';
import { computed, ref } from 'vue';
import { formatPhoneNumber } from '~/components/filters';
import PageLoading from '~/components/general/PageLoading.vue';
import KioskTeamCell from '~/components/kiosk/KioskTeamCell.vue';
import KioskTcrCampaignAttachModal from '~/components/kiosk/kiosk-ten-dlc/registration/KioskTcrCampaignAttachModal.vue';
import KioskTcrNumberDetachModal from '~/components/kiosk/kiosk-ten-dlc/registration/KioskTcrNumberDetachModal.vue';
import {
    phoneNumberOrderStatusColors,
    phoneNumberOrderStatusLabels,
    registrationStatusColors,
    registrationStatusLabels,
    tnOptionOrderStatusLabels,
} from '~/components/kiosk/kiosk-ten-dlc/registration/tenDlcRegistrationLabels';
import { useToasted } from '~/composables/useToasted';
import { adminTenDlcRegistrationClient } from '~/services';
import type {
    BandwidthTnOptionOrder,
    BandwidthTnOptionOrderError,
    TcrCampaignPhoneNumber,
    TcrCampaignPhoneNumbers,
    TcrPhoneNumberOrder,
} from '~/services/adminTenDlcRegistrationClient.types';
import { fromNow, shortDateTime } from '~/utils/date';
import dayjs from '~/utils/dayjs';
import { catchRequestError } from '~/utils/request';

// The sync re-checks open orders every fifteen minutes, so one open this long is still at Bandwidth.
const STALE_ORDER_MINUTES = 60;

const props = withDefaults(defineProps<{ campaignId: string; expired?: boolean }>(), { expired: false });

const toasted = useToasted();

const numbers = ref<TcrCampaignPhoneNumbers | null>(null);
const loading = ref(true);
const order = ref<BandwidthTnOptionOrder>();
const attaching = ref(false);
const detaching = ref<TcrCampaignPhoneNumber>();

const numbersCancelKey = Symbol('kiosk-tcr-campaign-numbers');

const load = async () => {
    loading.value = true;

    try {
        const { data } = await adminTenDlcRegistrationClient.getCampaignPhoneNumbers(props.campaignId, {
            cancelKey: numbersCancelKey,
        });
        numbers.value = data;
        loading.value = false;
    } catch (e) {
        if (isCancel(e)) {
            return;
        }

        loading.value = false;
        toasted.error(catchRequestError(e), { sticky: true });
    }
};

const orderTime = (order: TcrPhoneNumberOrder): string => order.settled_at ?? order.placed_at;

const orderErrorLine = (error: BandwidthTnOptionOrderError): string =>
    `${null === error.phone_number ? 'order' : formatPhoneNumber(error.phone_number)} · ${error.code} · ${
        error.description
    }`;

const orderFailed = (order: TcrPhoneNumberOrder | null): boolean =>
    null !== order && (['failed', 'exception'].includes(order.status) || null !== order.error_description);

const orderElsewhere = (order: TcrPhoneNumberOrder): boolean =>
    null !== order.campaign_id && order.campaign_id !== props.campaignId;

const orderStale = (order: TcrPhoneNumberOrder | null): boolean =>
    null !== order &&
    ['received', 'processing'].includes(order.status) &&
    dayjs().diff(dayjs.utc(order.placed_at), 'minute') >= STALE_ORDER_MINUTES;

const failedCount = computed(
    () =>
        (numbers.value?.data.filter((row) => orderFailed(row.last_order)).length ?? 0) +
        (numbers.value?.orders.filter(orderFailed).length ?? 0)
);

const orderSettled = computed(() => {
    const recorded = [...(numbers.value?.data.map((row) => row.last_order) ?? []), ...(numbers.value?.orders ?? [])];

    return recorded.some(
        (recordedOrder) =>
            null !== recordedOrder &&
            recordedOrder.order_id === order.value?.order_id &&
            !['received', 'processing'].includes(recordedOrder.status)
    );
});

const attachDisabledReason = computed(() => {
    if (null === numbers.value) {
        return null;
    }

    if (props.expired) {
        return 'Campaign is deactivated at TCR';
    }

    if ('tcr' !== numbers.value.source) {
        return 'Twilio-registered campaign';
    }

    if (null === numbers.value.dca_completed_at) {
        return 'Numbers cannot be attached until DCA complete is Yes';
    }

    if (numbers.value.data.length >= numbers.value.limit) {
        return `At the ${numbers.value.limit}-number limit; a number pool is a Bandwidth ticket`;
    }

    return null;
});

const detachModal = computed({
    get: () => undefined !== detaching.value,
    set: (open: boolean) => {
        if (!open) {
            detaching.value = undefined;
        }
    },
});

const onPlaced = (placed: BandwidthTnOptionOrder) => {
    attaching.value = false;
    detaching.value = undefined;
    order.value = placed;
    toasted.success('Order placed; Bandwidth is applying it.');
    load();
};

const onStale = () => {
    detaching.value = undefined;
    load();
};

load();
</script>
